import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './LandingPage.css';

// Import Font Awesome
import '@fortawesome/fontawesome-free/css/all.min.css';

// Import Google Fonts in index.html

const LandingPage = () => {
  const { user, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Add animation class to elements when they come into view
    const animatedElements = document.querySelectorAll('.feature-card, .testimonial, .about-content, .about-image');
    
    function checkIfInView() {
      animatedElements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('animate');
        }
      });
    }
    
    window.addEventListener('scroll', checkIfInView);
    checkIfInView(); // Check on initial load
    
    // Smooth scrolling for navigation and footer links
    const scrollLinks = document.querySelectorAll('.landing-nav a[href^="#"]:not([href="#"]), .footer-column a[href^="#"]:not([href="#"])');
    
    scrollLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        
        const targetId = this.getAttribute('href');
        const targetElement = document.querySelector(targetId);
        
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop - 80,
            behavior: 'smooth'
          });
        }
      });
    });
    
    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener('scroll', checkIfInView);
      scrollLinks.forEach(link => {
        link.removeEventListener('click', () => {});
      });
    };
  }, []);

  const handleLogin = () => {
    loginWithGoogle();
  };

  const handleSignUp = () => {
    loginWithGoogle();
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/');
    } else {
      loginWithGoogle();
    }
  };

  return (
    <div className="landing-page">
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="logo">
            <a href="#"><img src="/landingPage/Images/Devboard_Logo.png" alt="DevBoard Logo" className="logo-img" /></a>
          </div>
          <ul className="nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#testimonials">Testimonials</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
          <div className="cta-buttons">
            {user ? (
              <div className="profile-section">
                <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
                <img
                  src={user.photoURL || 'default-profile-icon.png'}
                  alt="Profile"
                  className="profile-picture"
                />
                <div className="user-dropdown">
                  <span className="user-name">{user.displayName}</span>
                  <button className="logout-btn" onClick={logout}>Logout</button>
                </div>
              </div>
            ) : (
              <>
                <a href="#" className="btn btn-secondary" onClick={handleLogin}>Log In</a>
                <a href="#" className="btn btn-primary" onClick={handleSignUp}>Sign Up</a>
              </>
            )}
          </div>
        </nav>
      </header>

      <section className="hero">
        <div className="hero-content">
          <h1>Game Development, Visualized</h1>
          <p>Streamline your game development workflow with powerful visualization tools and collaborative features.</p>
          <div className="hero-buttons">
            {user ? (
              <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
            ) : (
              <a href="#" className="btn btn-primary" onClick={handleGetStarted}>Get Started</a>
            )}
          </div>
        </div>
        <div className="hero-image">
          <div className="dashboard-preview">
            <div className="dashboard-header">
              <div className="dashboard-dot"></div>
              <div className="dashboard-dot"></div>
              <div className="dashboard-dot"></div>
            </div>
            <div className="dashboard-content">
              <div className="dashboard-sidebar">
                <div className="sidebar-item active"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
              </div>
              <div className="dashboard-main">
                <div className="dashboard-card"></div>
                <div className="dashboard-card"></div>
                <div className="dashboard-card"></div>
                <div className="dashboard-card"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <h2>Powerful Features</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-project-diagram"></i>
            </div>
            <h3>Visual Workflows</h3>
            <p>Create and manage game development workflows with intuitive visual tools.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-users"></i>
            </div>
            <h3>Team Collaboration</h3>
            <p>Collaborate seamlessly with your team in real-time.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <h3>Progress Tracking</h3>
            <p>Monitor your game development progress with detailed analytics.</p>
          </div>
        </div>
      </section>

      <section id="about" className="about">
        <div className="about-content">
          <h2>About DevBoard</h2>
          <p>DevBoard is a comprehensive game development platform designed to help developers visualize, manage, and streamline their game development process. Our tools make it easier to collaborate, track progress, and bring your game ideas to life.</p>
          <p>Whether you're an indie developer or part of a large studio, DevBoard provides the tools you need to succeed.</p>
        </div>
        <div className="about-image">
          <div className="image-container">
            <div className="floating-element elem1"></div>
            <div className="floating-element elem2"></div>
            <div className="floating-element elem3"></div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="testimonials">
        <h2>What Developers Say</h2>
        <div className="testimonial-slider">
          <div className="testimonial">
            <div className="testimonial-content">
              <p>"As a game developer I love the idea of Devboard"</p>
            </div>
            <div className="testimonial-author">
              <div className="author-avatar"></div>
              <div className="author-info">
                <h4>Anas AlBaloushi</h4>
                <p>Game Developer</p>
              </div>
            </div>
          </div>
          <div className="testimonial">
            <div className="testimonial-content">
              <p>"As a teacher, I see the potential in DevBoard, and it can help students create independent projects"</p>
            </div>
            <div className="testimonial-author">
              <div className="author-avatar"></div>
              <div className="author-info">
                <h4>Haitham Ghoussainy</h4>
                <p>Computer Science Professor</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Visualize Your Game Development?</h2>
          <p>Join thousands of developers who are using DevBoard to streamline their workflow.</p>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
          ) : (
            <a href="#" className="btn btn-primary" onClick={handleGetStarted}>Get Started Today</a>
          )}
        </div>
      </section>

      <footer id="contact" className="landing-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/landingPage/Images/Devboard_Logo.png" alt="DevBoard Logo" className="footer-logo-img" />
            <p>Game Development, Visualized</p>
          </div>
          <div className="footer-links">
          <div className="footer-column">
            <h3>Company</h3>
            <ul>
              <li><a href="#about">About Us</a></li>
            </ul>
          </div>
          <div className="footer-column">
            <h3>Contact Us</h3>
            <ul>
              <li><a href="mailto:eyadhqzidan@gmail.com">eyadhqzidan@gmail.com</a></li>
            </ul>
          </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 DevBoard. All rights reserved.</p>
          <div className="social-links">
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
